/**
 * Reglas de los puntos de seguimiento de un servicio: cuáles forman el recorrido del vehículo
 * y qué parte de su observación aporta algo cuando se pinta el globo del marcador.
 *
 * Viven acá, sueltas y sin dependencias, porque las comparten los dos mapas que muestran un
 * recorrido —el del servicio y el global— y porque así se prueban sin levantar un componente.
 */

/**
 * Marcadores que el mapa agrega a la polilínea sin ser parte del recorrido del vehículo:
 * señalan dónde empieza y dónde termina el servicio, no por dónde pasó.
 */
export const ORIGIN_MARKER_KEY = 'originMarker';
export const DESTINATION_MARKER_KEY = 'destinationMarker';

const ROUTE_MARKER_KEYS = [ORIGIN_MARKER_KEY, DESTINATION_MARKER_KEY];

/** Prefijo con el que el backend concatena el estado del vehículo dentro de la observación. */
const VEHICLE_STATE_PREFIX = 'Estado Vehiculo ';

/**
 * Los puntos que sí reportó el vehículo, sin los marcadores de origen y destino.
 *
 * Se filtra por la llave del marcador y no por su posición en el arreglo: el de destino solo
 * se agrega cuando el servicio tiene más de una dirección, así que contar desde el final
 * señalaba el punto equivocado justo en los servicios que no lo traen.
 */
export function trackingPathOnly<T extends { key?: string }>(points: T[]): T[] {
  if (!points || !points.length) return [];
  return points.filter(point => point && ROUTE_MARKER_KEYS.indexOf(point.key) === -1);
}

/** Último punto reportado por el vehículo, o `null` si el recorrido todavía no tiene ninguno. */
export function lastTrackingPoint<T extends { key?: string }>(points: T[]): T | null {
  const path = trackingPathOnly(points);
  return path.length ? path[path.length - 1] : null;
}

/**
 * Quita de la observación de un punto el estado del vehículo que el globo ya está mostrando
 * por su cuenta (como chip o como título).
 *
 * La integración GPS arma la observación pegando `Estado Vehiculo <estado>` delante de lo que
 * realmente aporta el punto, así que ese pedazo llega duplicado al globo. Se borra únicamente
 * el estado que se recibe por parámetro —el que ya está en pantalla—, nunca un patrón
 * adivinado: así una observación escrita por un conductor pasa siempre intacta.
 *
 * Lo que sobra (por ejemplo `Velocidad 45`) se conserva. Si no sobra nada devuelve cadena
 * vacía, y `buildMapInfoWindow` se encarga de no pintar el pie.
 */
export function stripVehicleStateObservation(observation: string, state: string): string {
  if (!observation) return '';
  if (!state) return normalize(observation);

  const phrase = VEHICLE_STATE_PREFIX + state;
  const index = observation.toLowerCase().indexOf(phrase.toLowerCase());
  if (index === -1) return normalize(observation);

  return normalize(observation.slice(0, index) + observation.slice(index + phrase.length));
}

/** El backend separa cada segmento con un espacio suelto que queda huérfano al recortar. */
function normalize(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}
